import { useState, useMemo, useEffect, useRef } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  useCity,
  useOwnerType,
  useUtilityServices,
} from "@features/cx/consumerOnboarding/hooks";
import { normalizeDate } from "@shared/utils/dateUtils";
import { Search } from "lucide-react";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { PageHeader } from "@shared/components/PageHeader";
import { useMasterConsumerSearch } from "@features/cx/consumer-onboarding/hooks";

const BusinessAccountNumberSearch = ({ form }: { form: any }) => {
  const remoteUtilityId = getRemoteUtilityId();
  // Sync initial state if it comes from props
  const [searchTerm, setSearchTerm] = useState(
    form.getValues("businessAccountNumber") || "",
  );
  const [showDropdown, setShowDropdown] = useState(false);

  // Sync state if form changes dynamically (e.g. on reset)
  useEffect(() => {
    const subscription = form.watch((value: any, { name }: any) => {
      // name is undefined on form.reset()
      if (!name || name === "businessAccountNumber") {
        setSearchTerm(value.businessAccountNumber || "");
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const { data: consumerListResponse } = useMasterConsumerSearch({
    remoteUtilityId: remoteUtilityId,
    searchData: searchTerm,
    requestType: "",
  });

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        type="text"
        className="w-full h-11 px-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
        placeholder="Search consumer number..."
        value={searchTerm}
        onChange={(e) => {
          const val = e.target.value;
          setSearchTerm(val);
          setShowDropdown(val.length >= 2);
          form.setValue("businessAccountNumber", val, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        onFocus={() => setShowDropdown(searchTerm.length >= 2)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        autoComplete="off"
      />
      {showDropdown && consumerListResponse?.result?.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {consumerListResponse.result.map((consumer: any) => (
            <div
              key={consumer.id || consumer.consumerNo}
              className="p-3 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-b-0 transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                const fullName =
                  `${consumer.firstName || ""} ${consumer.lastName || ""}`.trim();
                const accountId = consumer.id || "";
                console.warn("accountId", accountId);
                form.setValue("businessAccountNumber", consumer.consumerNo, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                form.setValue("businessAccountName", fullName, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                form.setValue("businessAccountId", accountId, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                setSearchTerm(consumer.consumerNo);
                setShowDropdown(false);
              }}
            >
              <div className="font-medium text-gray-900">
                {consumer.consumerNo}
              </div>
              <div className="text-gray-500 text-xs mt-0.5">
                {consumer.firstName} {consumer.lastName}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface ServiceDetailProps {
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  consumerData?: any;
  onFormReady?: (form: any) => void;
}

export function ServiceDetail({
  onDataChange,
  onValidationChange,
  consumerData,
  onFormReady,
}: ServiceDetailProps) {
  const { data: utilityData } = useUtilityServices();
  const { data: cityData } = useCity();
  // Add a ref to track if we're currently updating fields programmatically
  const isUpdatingRef = useRef(false);
  const { data: ownerTypeData } = useOwnerType();
  const ownerTypeOptions = useMemo(
    () =>
      ownerTypeData?.result?.map((ownerType) => ({
        value: ownerType.key,
        label: ownerType.value,
      })) || [],
    [ownerTypeData],
  );
  const cityOptions = useMemo(
    () =>
      cityData?.result?.map((city) => ({
        value: city.code,
        label: city.name,
      })) || [],
    [cityData],
  );

  const checkIsMeterRequired = (utility: any) => {
    if (!utility) return false;
    const val =
      utility.isMeterRequired ??
      utility.is_meter_required ??
      utility.isMetered ??
      utility.is_metered ??
      utility.metered ??
      utility.isMeter ??
      utility.is_meter;

    if (val === true || val === 1 || val === "true" || val === "1") {
      return true;
    }
    if (val === false || val === 0 || val === "false" || val === "0") {
      return false;
    }
    return false;
  };

  const getUtilityOptions = useMemo(() => {
    const list = Array.isArray(utilityData)
      ? utilityData
      : utilityData?.result || [];
    if (!Array.isArray(list)) return [];
    return list
      .filter(
        (utility: any) =>
          utility && (utility.name || utility.label || utility.value),
      )
      .map((utility: any) => {
        const name = utility.name || utility.label || utility.value;
        return {
          value: name,
          label: utility.label || utility.name || name,
          isMeterRequired: checkIsMeterRequired(utility),
        };
      });
  }, [utilityData]);

  const formFields: FormField[] = useMemo(
    () => [
      // ==================== SERVICE DETAILS SECTION ====================
      {
        name: "serviceStartDate",
        label: "Service Start Date",
        type: "datepicker",
        required: true,
        placeholder: "dd-mm-yyyy",
        helperText: "Select when you want service to begin",
        // group: "Service Details",
        className: "col-span-4",
      },
      {
        name: "preferredContactMethod",
        label: "Preferred Contact Method",
        type: "multi-select",
        options: [
          { label: "Email", value: "Email" },
          { label: "Phone", value: "Phone" },
        ],
        required: true,
        helperText: "Choose how you'd like us to contact you",
        // group: "Service Details",
        className: "col-span-4",
      },
      {
        name: "selectServicesHeader",
        label: "Select Services",
        type: "multi-select",
        required: true,
        // group: "Service Details",
        options: getUtilityOptions,
        groupColorNeeded: false,
        className: "col-span-4",
      },
      ...getUtilityOptions.map((option: any) => ({
        name: `service_quantity_${option.value}`,
        label: option.label,
        type: "stepper" as const,
        required: true,
        min: 1,
        max: 999,
        defaultValue: 1,
        helperText: "Number of connections / units",
        // group: "Service Details",
        groupColorNeeded: false,
        className: "col-span-2",
        showWhen: (formValues: any) => {
          if (option.isMeterRequired === false) {
            return false;
          }
          const selectedRaw = formValues?.selectServicesHeader;
          if (!selectedRaw) return false;
          const selected = Array.isArray(selectedRaw)
            ? selectedRaw
            : [selectedRaw];
          return selected.some((item: any) => {
            const val =
              typeof item === "object" && item !== null
                ? item.value || item.label
                : item;
            return (
              String(val).trim().toLowerCase() ===
                String(option.value).trim().toLowerCase() ||
              String(val).trim().toLowerCase() ===
                String(option.label).trim().toLowerCase()
            );
          });
        },
      })),
      // ==================== PROPERTY TYPE SECTION ====================
      {
        name: "propertyType",
        label: "Property Type & Account Structure",
        type: "radio-card",
        required: true,
        helperText:
          "Select the account type based on property ownership and usage",

        className: "col-span-12",
        options: [
          {
            value: "individual",
            label: "Individual Property Owner",
            description:
              "Single property with one owner (residential, commercial, or industrial)",
          },
          {
            value: "tenant",
            label: "Tenant/Renter Account",
            description:
              "Applicant is renting the property - landlord/owner information required",
          },
          {
            value: "business",
            label: "Multi-Property Business Account",
            description:
              "Business entity with multiple service locations (can be linked to master account)",
          },
        ],
      },
      // ==================== OWNER DETAILS SECTION (conditional) ====================
      {
        name: "ownerName",
        label: "Owner Name",
        type: "text",
        required: true,
        placeholder: "Enter owner's full name",
        group: "Owner Details",
        groupHelperText: "Provide information about the property owner",
        groupColorNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) => formValues.propertyType === "tenant",
        groupBorderNeeded: false,
      },
      {
        name: "ownerPhone",
        label: "Owner Phone Number",
        type: "country-phone",
        required: true,
        placeholder: "+1 (555) 000-0000",
        group: "Owner Details",
        groupColorNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) => formValues.propertyType === "tenant",
        groupBorderNeeded: false,
      },
      {
        name: "ownerEmail",
        label: "Owner Email",
        type: "email",
        required: false,
        placeholder: "owner@example.com",
        group: "Owner Details",
        groupColorNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) => formValues.propertyType === "tenant",
        groupBorderNeeded: false,
      },
      {
        name: "ownerAddress",
        label: "Owner Address",
        type: "text",
        required: true,
        placeholder: "Enter owner's full address",
        group: "Owner Details",
        groupColorNeeded: false,
        className: "col-span-12",
        showWhen: (formValues) => formValues.propertyType === "tenant",
        groupBorderNeeded: false,
      },
      // ==================== BUSINESS ACCOUNT SECTION (conditional) ====================
      {
        name: "businessAccountLink",
        label:
          "Do you want to link this account to an existing business account?",
        type: "radio-card",
        required: true,
        className: "col-span-12",
        showWhen: (formValues) => formValues.propertyType === "business",
        options: [
          {
            value: "new",
            label: "No, create this as a new main business account",
            description: "This will be set up as a Master Account",
          },
          {
            value: "existing",
            label: "Yes, link to an existing business account",
            description: "Connect this location to an existing account",
          },
        ],
      },
      // ==================== EXISTING BUSINESS ACCOUNT DETAILS (conditional) ====================
      {
        name: "businessAccountNumber",
        label: "Business Account Number",
        type: "custom",
        required: true,
        customRender: (field, form, error) => (
          <BusinessAccountNumberSearch form={form} />
        ),

        placeholder: "Enter account number",
        helperText: "Enter the existing business account number",
        group: "Existing Business Account Details",
        groupHelperText:
          "Provide the details of the existing business account you want to link to",
        groupColorNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) =>
          formValues.propertyType === "business" &&
          formValues.businessAccountLink === "existing",
      },
      {
        name: "businessAccountName",
        label: "Business Account Name",
        type: "text",
        required: true,
        disabled: true,
        placeholder: "Enter business account name",
        helperText: "Name of the business account",
        group: "Existing Business Account Details",
        groupColorNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) =>
          formValues.propertyType === "business" &&
          formValues.businessAccountLink === "existing",
      },
      {
        name: "businessAccountId",
        type: "hidden",
      },
      // ==================== SERVICE ADDRESS SECTION ====================
      {
        name: "serviceHouseUnit",
        label: "House/Unit Number",
        type: "text",
        required: true,
        placeholder: "e.g. 123, Apt 4B",
        helperText: "Enter your building or unit identifier",
        group: "Service Address",
        className: "col-span-4",
        groupColorNeeded: false,
        groupBorderNeeded: false,
      },
      {
        name: "serviceZipCode",
        label: "Zip/Postal Code",
        type: "text",
        required: false,
        placeholder: "e.g. 12345",
        helperText: "Enter your postal code",
        group: "Service Address",
        className: "col-span-4",
        groupBorderNeeded: false,
      },
      {
        name: "serviceCity",
        label: "City",
        type: "select",
        required: true,
        options: cityOptions,
        placeholder: "Enter city",
        helperText: "Enter your city",
        group: "Service Address",
        className: "col-span-4",
        groupBorderNeeded: false,
      },
      {
        name: "serviceStreetAddress",
        label: "Street Address",
        type: "text",
        required: true,
        placeholder: "e.g. 123 Main Street",
        helperText: "Enter your street address",
        group: "Service Address",
        className: "col-span-12",
        groupBorderNeeded: false,
      },
      {
        name: "sameAsServiceAddress",
        label: "Mailing address same as service address",
        type: "checkbox",

        group: "Mailing Address",
        groupHelperText:
          "Uncheck if your billing or mailing address is different from your service address",
        groupColorNeeded: false,
        className: "col-span-12",
        groupBorderNeeded: false,
      },
      // ==================== MAILING ADDRESS SECTION (conditional) ====================
      {
        name: "mailingHouseUnit",
        label: "House/Unit Number",
        type: "text",
        required: true,
        placeholder: "e.g. 123, Apt 4B",
        helperText: "Enter your building or unit identifier",
        group: "Mailing Address",
        className: "col-span-4",
        showWhen: (formValues) => !formValues.sameAsServiceAddress,
        groupColorNeeded: false,
        groupBorderNeeded: false,
      },
      {
        name: "mailingZipCode",
        label: "Zip/Postal Code",
        type: "text",
        required: false,
        placeholder: "e.g. 12345",
        helperText: "Enter your postal code",
        group: "Mailing Address",
        className: "col-span-4",
        showWhen: (formValues) => !formValues.sameAsServiceAddress,
        groupBorderNeeded: false,
      },
      {
        name: "mailingCity",
        label: "City",
        type: "select",
        required: true,
        options: cityOptions,
        placeholder: "Enter city",
        helperText: "Enter your city",
        group: "Mailing Address",
        className: "col-span-4",
        showWhen: (formValues) => !formValues.sameAsServiceAddress,
        groupBorderNeeded: false,
      },
      {
        name: "mailingStreetAddress",
        label: "Street Address",
        type: "text",
        required: true,
        placeholder: "e.g. 123 Main Street",
        helperText: "Enter your street address",
        group: "Mailing Address",
        className: "col-span-12",
        showWhen: (formValues) => !formValues.sameAsServiceAddress,
        groupBorderNeeded: false,
      },
      // ==================== BILLING PREFERENCES SECTION ====================
      {
        name: "paperlessBilling",
        label: "Enroll in Paperless Billing (E-Bill)",
        type: "checkbox",
        helperText:
          "Receive your bills electronically via email instead of paper statements",
        group: "Billing Preferences",
        className: "col-span-12",
        groupColorNeeded: false,
      },
    ],
    [getUtilityOptions, cityOptions],
  );

  const defaultFormValues = useMemo(() => {
    if (!consumerData?.result) {
      return {
        sameAsServiceAddress: true,
        paperlessBilling: false,
      };
    }

    const data = consumerData.result;
    const serviceAddress = data.addressData?.service || {};
    const billingAddress = data.addressData?.billing || {};

    const isSameAddress =
      serviceAddress.UNIT === billingAddress.UNIT &&
      serviceAddress.ZIPCODE === billingAddress.ZIPCODE &&
      serviceAddress.CITY === billingAddress.CITY &&
      serviceAddress.ADDRESS === billingAddress.ADDRESS;

    const ownershipDetails = data.ownershipDetails || {};
    const tenantDetail = ownershipDetails.ownershipDetail || {};

    let propertyType = "individual";
    if (ownershipDetails.structureChoice === "Tenant") {
      propertyType = "tenant";
    } else if (
      ownershipDetails.structureChoice === "Master" ||
      ownershipDetails.structureChoice === "Sub_account"
    ) {
      propertyType = "business";
    }

    let businessAccountLink = "new";
    if (ownershipDetails.structureChoice === "Sub_account") {
      businessAccountLink = "existing";
    }

    const availableOptions = getUtilityOptions.map((opt) => opt.value);
    const rawUtilityService =
      data.selectServicesHeader ||
      data.selected_services ||
      data.utilityService ||
      data.utility_service ||
      data.primary_consumer?.utility_service ||
      data.utility_services;

    const utilityServiceVal = Array.isArray(rawUtilityService)
      ? rawUtilityService
      : typeof rawUtilityService === "string"
        ? [rawUtilityService]
        : typeof rawUtilityService === "object" && rawUtilityService !== null
          ? Object.keys(rawUtilityService)
          : [];

    const quantityValues: Record<string, any> = {};
    const allServicesToInit = Array.isArray(utilityServiceVal)
      ? utilityServiceVal
      : [];

    allServicesToInit.forEach((srv: string) => {
      const matchingOpt = getUtilityOptions.find(
        (opt: any) =>
          String(opt.value).trim().toLowerCase() ===
            String(srv).trim().toLowerCase() ||
          String(opt.label).trim().toLowerCase() ===
            String(srv).trim().toLowerCase(),
      );
      const isMetered = matchingOpt ? matchingOpt.isMeterRequired === true : false;
      const count =
        data?.additionalData?.utilityServiceQuantityMapping?.[srv] ??
        data?.additional_data?.utility_service_quantity_mapping?.[srv] ??
        data?.number_of_connections?.[srv] ??
        data?.service_quantities?.[srv] ??
        data?.[`service_quantity_${srv}`] ??
        data?.numberOfConnections?.[srv] ??
        data?.serviceQuantities?.[srv] ??
        (isMetered ? 1 : 0);
      quantityValues[`service_quantity_${srv}`] = isMetered
        ? Number(count) || 1
        : 0;
    });

    return {
      serviceStartDate: normalizeDate(data.registrationDate) || "",
      preferredContactMethod: data.preferredContactMethod || [],
      selectServicesHeader: utilityServiceVal,
      ...quantityValues,

      propertyType,
      ownerName: tenantDetail.ownerName || "",
      ownerPhone: tenantDetail.ownerPhone || "",
      ownerEmail: tenantDetail.ownerEmail || "",
      ownerType: tenantDetail.ownerType || "",
      ownerAddress: tenantDetail.ownerAddress || "",

      businessAccountLink,
      businessAccountNumber:
        ownershipDetails.masterAccountNo ||
        ownershipDetails.appliedMasterAccountNo ||
        "",
      businessAccountName:
        ownershipDetails.masterAccountName ||
        ownershipDetails.appliedMasterAccountEmail ||
        "",

      // Service Address - from addressData.service
      serviceHouseUnit: serviceAddress?.UNIT || "",
      serviceZipCode: serviceAddress?.ZIPCODE || "",
      serviceCity: serviceAddress?.CITY || "",
      serviceStreetAddress: serviceAddress?.ADDRESS || "",

      sameAsServiceAddress: isSameAddress,
      paperlessBilling: data.isEBill || false,

      // Mailing Address - from addressData.billing (only if different)
      mailingHouseUnit: isSameAddress ? "" : billingAddress?.UNIT || "",
      mailingZipCode: isSameAddress ? "" : billingAddress?.ZIPCODE || "",
      mailingCity: isSameAddress ? "" : billingAddress?.CITY || "",
      mailingStreetAddress: isSameAddress ? "" : billingAddress?.ADDRESS || "",
    };
  }, [consumerData, getUtilityOptions]);

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

  // Ensure stepper fields are initialized with 1 if they are empty
  const selectedServicesHeader = form.watch("selectServicesHeader");
  useEffect(() => {
    if (Array.isArray(selectedServicesHeader)) {
      selectedServicesHeader.forEach((srv: any) => {
        const valObj =
          typeof srv === "object" && srv !== null
            ? srv.value || srv.label
            : srv;

        const matchingOpt = getUtilityOptions.find(
          (opt: any) =>
            String(opt.value).trim().toLowerCase() ===
              String(valObj).trim().toLowerCase() ||
            String(opt.label).trim().toLowerCase() ===
              String(valObj).trim().toLowerCase(),
        );

        if (!matchingOpt || matchingOpt.isMeterRequired !== true) {
          return;
        }

        const fieldName = `service_quantity_${valObj}`;
        const currentVal = form.getValues(fieldName);
        if (
          currentVal === undefined ||
          currentVal === null ||
          currentVal === ""
        ) {
          form.setValue(fieldName, 1, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      });
    }
  }, [selectedServicesHeader, form, getUtilityOptions]);

  // Reset form and populate data when consumer data changes
  useEffect(() => {
    if (!consumerData?.result) return;

    // allow fields to mount based on showWhen
    setTimeout(() => {
      FormService.populateForm(form, defaultFormValues, formFields);
      const currentValues = form.getValues();
      if (onDataChange) {
        onDataChange(buildPayload(currentValues));
      }
    }, 0);
  }, [consumerData, defaultFormValues, form]);

  // Sync data to parent component on mount and whenever form changes
  useEffect(() => {
    if (!onDataChange) return;
    const currentValues = form.getValues();
    onDataChange(buildPayload(currentValues));
    const subscription = form.watch((value) => {
      onDataChange(buildPayload({ ...form.getValues(), ...value }));
    });
    return () => subscription.unsubscribe();
  }, [form, onDataChange, getUtilityOptions]);

  const buildPayload = (formValues: any) => {
    const getCityLabel = (cityCode: string) => {
      return (
        cityOptions.find((option) => option.value === cityCode)?.label || ""
      );
    };
    const selectedServices = formValues.selectServicesHeader || [];
    const number_of_connections: Record<string, number> = {};
    const service_quantities: Record<string, number> = {};
    if (Array.isArray(selectedServices)) {
      selectedServices.forEach((srv: string) => {
        const srvName =
          typeof srv === "object" && srv !== null
            ? (srv as any).value || (srv as any).label
            : srv;

        const matchingOpt = getUtilityOptions.find(
          (opt: any) =>
            String(opt.value).trim().toLowerCase() ===
              String(srvName).trim().toLowerCase() ||
            String(opt.label).trim().toLowerCase() ===
              String(srvName).trim().toLowerCase(),
        );

        if (!matchingOpt || matchingOpt.isMeterRequired !== true) {
          return;
        }

        const count =
          formValues[`service_quantity_${srvName}`] !== undefined &&
          !isNaN(Number(formValues[`service_quantity_${srvName}`]))
            ? Number(formValues[`service_quantity_${srvName}`])
            : 1;
        number_of_connections[srvName] = count;
        service_quantities[srvName] = count;
      });
    }

    const utility_service: Record<string, number> = {};
    if (Array.isArray(selectedServices)) {
      selectedServices.forEach((srv: string) => {
        const srvName =
          typeof srv === "object" && srv !== null
            ? (srv as any).value || (srv as any).label
            : srv;

        const matchingOpt = getUtilityOptions.find(
          (opt: any) =>
            String(opt.value).trim().toLowerCase() ===
              String(srvName).trim().toLowerCase() ||
            String(opt.label).trim().toLowerCase() ===
              String(srvName).trim().toLowerCase(),
        );

        const isMetered = matchingOpt ? matchingOpt.isMeterRequired === true : false;
        utility_service[srvName] = isMetered
          ? (number_of_connections[srvName] ?? 1)
          : 0;
      });
    }

    const payload: any = {
      service_start_date: formValues.serviceStartDate || "",
      preferred_contact_methods: formValues.preferredContactMethod || [],
      selectServicesHeader: selectedServices,
      selected_services: selectedServices,
      utility_service,
      number_of_connections,
      service_quantities,
      ...Object.fromEntries(
        Object.entries(number_of_connections).map(([k, v]) => [
          `service_quantity_${k}`,
          v,
        ]),
      ),

      property_type: formValues.propertyType || "",
      service_address: {
        house_unit_number: formValues.serviceHouseUnit || "",
        zip_postal_code: formValues.serviceZipCode || "",
        city: formValues.serviceCity || "",
        city_display: getCityLabel(formValues.serviceCity),
        street_address: formValues.serviceStreetAddress || "",
      },
      same_as_service_address: formValues.sameAsServiceAddress || false,
      paperless_billing: formValues.paperlessBilling || false,
      ownerDetails: {
        name: formValues.ownerName || "",
        phone: formValues.ownerPhone || "",
        email: formValues.ownerEmail || null,
        type: formValues.ownerType || "",
        address: formValues.ownerAddress || "",
      },
      business_account_link: formValues.businessAccountLink || "",
      existing_business_account: {
        account_number: formValues.businessAccountNumber || "",
        account_email: formValues.businessAccountName || "",
        account_id: formValues.businessAccountId || "",
      },
    };

    // If "Same as service address" is checked, copy service address to mailing address
    if (formValues.sameAsServiceAddress) {
      payload.mailing_address = {
        house_unit_number: formValues.serviceHouseUnit || "",
        zip_postal_code: formValues.serviceZipCode || "",
        city: formValues.serviceCity || "",
        city_display: getCityLabel(formValues.serviceCity),
        street_address: formValues.serviceStreetAddress || "",
      };
    } else {
      // Otherwise use the separate mailing address fields
      payload.mailing_address = {
        house_unit_number: formValues.mailingHouseUnit || "",
        zip_postal_code: formValues.mailingZipCode || "",
        city: formValues.mailingCity || "",
        city_display: getCityLabel(formValues.mailingCity),
        street_address: formValues.mailingStreetAddress || "",
      };
    }

    return payload;
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // Prevent recursive calls
    if (isUpdatingRef.current) {
      return;
    }

    try {
      isUpdatingRef.current = true;

      // When "Same as service address" is checked, copy service address to mailing address
      if (fieldName === "sameAsServiceAddress" && value === true) {
        const serviceHouseUnit = form.getValues("serviceHouseUnit");
        const serviceZipCode = form.getValues("serviceZipCode");
        const serviceCity = form.getValues("serviceCity");
        const serviceStreetAddress = form.getValues("serviceStreetAddress");

        form.setValue("mailingHouseUnit", serviceHouseUnit, {
          shouldValidate: false,
          shouldDirty: false,
        });
        form.setValue("mailingZipCode", serviceZipCode, {
          shouldValidate: false,
          shouldDirty: false,
        });
        form.setValue("mailingCity", serviceCity, {
          shouldValidate: false,
          shouldDirty: false,
        });
        form.setValue("mailingStreetAddress", serviceStreetAddress, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }

      // When "Same as service address" is unchecked, clear mailing address fields
      if (fieldName === "sameAsServiceAddress" && value === false) {
        form.setValue("mailingHouseUnit", "", {
          shouldValidate: false,
          shouldDirty: false,
        });
        form.setValue("mailingZipCode", "", {
          shouldValidate: false,
          shouldDirty: false,
        });
        form.setValue("mailingCity", "", {
          shouldValidate: false,
          shouldDirty: false,
        });
        form.setValue("mailingStreetAddress", "", {
          shouldValidate: false,
          shouldDirty: false,
        });
      }

      // When service address fields change and "Same as service address" is checked,
      // update the mailing address fields as well
      const serviceAddressFields = [
        "serviceHouseUnit",
        "serviceZipCode",
        "serviceCity",
        "serviceStreetAddress",
      ];

      if (
        serviceAddressFields.includes(fieldName) &&
        form.getValues("sameAsServiceAddress") === true
      ) {
        const mailingFieldMap: Record<string, string> = {
          serviceHouseUnit: "mailingHouseUnit",
          serviceZipCode: "mailingZipCode",
          serviceCity: "mailingCity",
          serviceStreetAddress: "mailingStreetAddress",
        };

        const mailingField = mailingFieldMap[fieldName];
        if (mailingField) {
          form.setValue(mailingField as any, value, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      }

      if (onDataChange) {
        const currentValues = form.getValues();
        onDataChange(buildPayload(currentValues));
      }
    } finally {
      // Always reset the flag in finally block
      isUpdatingRef.current = false;
    }
  };

  // Watch form validation state
  useEffect(() => {
    if (onValidationChange) {
      const subscription = form.watch(() => {
        const errors = form.formState.errors;
        const isValid = Object.keys(errors).length === 0;
        onValidationChange(isValid);
      });
      return () => subscription.unsubscribe();
    }
  }, [form, onValidationChange]);

  const getServiceCityLabel = () => {
    const cityValue = form.watch("serviceCity");
    return (
      cityOptions.find((option) => option.value === cityValue)?.label || ""
    );
  };

  const getMailingCityLabel = () => {
    const cityValue = form.watch("mailingCity");
    return (
      cityOptions.find((option) => option.value === cityValue)?.label || ""
    );
  };

  return (
    <div className="space-y-6">
      <div className="mb-4 flex items-center gap-4">
        <div className="flex flex-col">
          <PageHeader
            title="Service Details"
            subtitle="Select the services you need and provide property information"
          />
        </div>
      </div>
      {/* Form Section */}
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={() => {}}
        onFieldChange={handleFieldChange}
        gridColumns={12}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: FIELD_CLASSES,
        }}
      />

      {/* Address Preview Section */}
      <div className="space-y-4 pt-6 border-t">
        <h2 className="text-l font-semibold text-gray-900">Address Preview</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Service Address Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-blue-900">
                Service Address
              </h3>
            </div>
            <p className="text-sm text-blue-700 mb-3">
              How your service address will appear:
            </p>
            <div className="space-y-1 text-sm text-blue-900">
              <p className="font-medium">
                {form.watch("serviceHouseUnit") || ""}
              </p>
              <p>{form.watch("serviceStreetAddress") || ""}</p>
              <p>
                {getServiceCityLabel()}
                {getServiceCityLabel() && form.watch("serviceZipCode") && ", "}
                {form.watch("serviceZipCode") || ""}
              </p>
            </div>
          </div>

          {/* Mailing Address Preview */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-green-900">
                Mailing Address
              </h3>
            </div>
            <p className="text-sm text-green-700 mb-3">
              How your mailing address will appear:
            </p>
            <div className="space-y-1 text-sm text-green-900">
              {form.watch("sameAsServiceAddress") ? (
                <>
                  <p className="font-medium">
                    {form.watch("serviceHouseUnit") || ""}
                  </p>
                  <p>{form.watch("serviceStreetAddress") || ""}</p>
                  <p>
                    {getServiceCityLabel()}
                    {getServiceCityLabel() &&
                      form.watch("serviceZipCode") &&
                      ", "}
                    {form.watch("serviceZipCode") || ""}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">
                    {form.watch("mailingHouseUnit") || ""}
                  </p>
                  <p>{form.watch("mailingStreetAddress") || ""}</p>
                  <p>
                    {getMailingCityLabel()}
                    {getMailingCityLabel() &&
                      form.watch("mailingZipCode") &&
                      ", "}
                    {form.watch("mailingZipCode") || ""}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
